import { useEffect, useState } from 'react';
import { Gift, Mail, Sparkles, Users } from 'lucide-react';
import { useDarkMode } from '@/hooks/useDarkMode';
import { api } from '@/App';

// Only show social proof once there are enough signups for it to feel real.
const MIN_WAITLIST_TO_SHOW = 3;

export default function ComingSoon({ user }) {
  const darkMode = useDarkMode();
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    api.get('/launch/waitlist-count')
      .then((res) => setWaitlistCount(res.data?.count ?? null))
      .catch(() => setWaitlistCount(null));
  }, []);

  const showCounter = waitlistCount !== null && waitlistCount >= MIN_WAITLIST_TO_SHOW;
  // Logged-in users are part of the count; show how many are waiting WITH them.
  const othersCount = user ? Math.max(waitlistCount - 1, 0) : waitlistCount;

  return (
    <div
      data-testid="coming-soon-section"
      className="min-h-[70vh] flex items-center justify-center px-6 py-20"
    >
      <div className="max-w-xl text-center">
        <div
          className="w-16 h-16 rounded-full mx-auto mb-8 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #9E6752 0%, #C97C5F 100%)' }}
        >
          <Gift className="w-8 h-8 text-white" />
        </div>
        <h1
          className="text-4xl sm:text-5xl mb-6"
          style={{ fontFamily: 'Playfair Display, serif', color: darkMode.isDark ? '#FFFAF5' : '#20212B' }}
        >
          Something thoughtful is on its way
        </h1>
        <p className="text-base mb-3" style={{ color: darkMode.isDark ? '#C9CBCE' : '#534145' }}>
          We're hand-picking every single gift before we open the doors. No filler,
          no clutter, just things worth giving.
        </p>
        <p className="text-base mb-10" style={{ color: darkMode.isDark ? '#C9CBCE' : '#534145' }}>
          {user
            ? "You're already on the list. The moment we open, you'll be the first to know."
            : 'Create a free account and be the first to know the moment we open.'}
        </p>
        {user ? (
          <div
            data-testid="coming-soon-on-list"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
            style={{ background: '#FED7A5', color: '#20212B' }}
          >
            <Sparkles className="w-4 h-4" />
            You're on the list, {(user.name || '').split(' ')[0] || 'friend'}
          </div>
        ) : (
          <button
            data-testid="coming-soon-signup-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('mementa:open-auth'))}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #9E6752 0%, #C97C5F 100%)' }}
          >
            <Mail className="w-4 h-4" />
            Notify me at launch
          </button>
        )}
        {showCounter && (
          <div
            data-testid="coming-soon-waitlist-counter"
            className="mt-6 flex items-center justify-center gap-2 text-sm"
            style={{ color: darkMode.isDark ? '#C9CBCE' : '#73766A' }}
          >
            <Users className="w-4 h-4" style={{ color: '#9E6752' }} />
            <span>
              {user
                ? `${othersCount} other gift-lover${othersCount === 1 ? '' : 's'} waiting with you`
                : `Join ${othersCount} other${othersCount === 1 ? '' : 's'} waiting for the doors to open`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
